import { useEffect, useState, type FormEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { Shell } from "../components/Shell";
import type {
  RecipePublic,
  RecipeCreate,
  RecipeUpdate,
} from "../lib/types";

interface DraftIng {
  key: string;
  raw_text: string;
}
interface DraftStep {
  key: string;
  text: string;
}

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function RecipeEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const nav = useNavigate();
  const qc = useQueryClient();

  const [title, setTitle] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [servings, setServings] = useState<number>(2);
  const [notes, setNotes] = useState("");
  const [ingredients, setIngredients] = useState<DraftIng[]>([
    { key: uid(), raw_text: "" },
  ]);
  const [steps, setSteps] = useState<DraftStep[]>([{ key: uid(), text: "" }]);
  const [error, setError] = useState<string | null>(null);

  const existing = useQuery<RecipePublic>({
    queryKey: ["recipe", id],
    queryFn: () => api.get<RecipePublic>(`/api/recipes/${id}`),
    enabled: !isNew,
  });

  useEffect(() => {
    if (!isNew && existing.data) {
      const r = existing.data;
      setTitle(r.title);
      setSourceUrl(r.source_url ?? "");
      setServings(r.servings);
      setNotes(r.notes ?? "");
      setIngredients(
        r.ingredients.length > 0
          ? r.ingredients.map((i) => ({ key: uid(), raw_text: i.raw_text }))
          : [{ key: uid(), raw_text: "" }],
      );
      setSteps(
        r.steps.length > 0
          ? r.steps.map((s) => ({ key: uid(), text: s.text }))
          : [{ key: uid(), text: "" }],
      );
    }
    // re-init only when the loaded recipe id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, existing.data?.id]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        title: title.trim(),
        source_url: sourceUrl.trim() || null,
        servings,
        notes: notes.trim() || null,
        ingredients: ingredients
          .filter((i) => i.raw_text.trim())
          .map((i) => ({ raw_text: i.raw_text.trim() })),
        steps: steps
          .filter((s) => s.text.trim())
          .map((s) => ({ text: s.text.trim() })),
      };
      if (isNew) {
        return api.post<RecipePublic>("/api/recipes", payload as RecipeCreate);
      }
      return api.patch<RecipePublic>(
        `/api/recipes/${id}`,
        payload as RecipeUpdate,
      );
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ["recipes"] });
      qc.invalidateQueries({ queryKey: ["recipe", String(saved.id)] });
      nav(`/recipes/${saved.id}`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setError(e.detail);
      else setError("Failed to save");
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError("A title is needed before this can go into the book.");
      return;
    }
    save.mutate();
  };

  return (
    <Shell>
      <form onSubmit={onSubmit} className="mx-auto">
        <header
          className="mb-16 grid grid-cols-12 gap-x-8 gap-y-10 border-b border-ink/80 pb-12 animate-fade-up"
          style={{ animationDelay: "60ms" }}
        >
          <div className="col-span-12 md:col-span-3">
            <div className="label mb-2 text-ink-walnut">Section</div>
            <div className="label text-sienna">
              {isNew ? "New entry" : `Revising N° ${id}`}
            </div>
          </div>
          <div className="col-span-12 md:col-span-9">
            <label className="label mb-3 block text-ink-walnut">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled"
              className="w-full border-0 border-b border-ink/30 bg-transparent py-3 font-display text-[clamp(2.5rem,6vw,5rem)] font-light leading-[0.95] tracking-[-0.02em] text-ink placeholder:text-ink-muted/40 focus:border-ink focus:outline-none"
            />
            <div className="mt-10 flex flex-wrap items-end gap-x-16 gap-y-8">
              <div className="flex-1 min-w-[280px]">
                <label className="label mb-2 block text-ink-walnut">
                  Source URL
                </label>
                <input
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="optional · where you found it"
                  className="field-input"
                />
              </div>
              <div>
                <label className="label mb-2 block text-ink-walnut">
                  Serves
                </label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={servings}
                  onChange={(e) =>
                    setServings(parseInt(e.target.value || "1", 10))
                  }
                  className="w-24 border-0 border-b border-ink/30 bg-transparent py-3 text-center font-display text-4xl font-light text-ink focus:border-ink focus:outline-none"
                />
              </div>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-12 gap-x-8 gap-y-16">
          <section
            className="col-span-12 md:col-span-4 animate-fade-up"
            style={{ animationDelay: "180ms" }}
          >
            <div className="mb-8 flex items-center gap-4">
              <div className="label text-sienna">N° 01</div>
              <div className="label text-ink-walnut">Ingredients</div>
              <span className="hairline-ink flex-1" />
            </div>
            <ul className="space-y-1">
              {ingredients.map((ing, idx) => (
                <li
                  key={ing.key}
                  className="group grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-ink/10 py-2"
                >
                  <span className="label w-7 text-ink-muted">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <input
                    type="text"
                    value={ing.raw_text}
                    onChange={(e) =>
                      setIngredients((prev) =>
                        prev.map((p) =>
                          p.key === ing.key
                            ? { ...p, raw_text: e.target.value }
                            : p,
                        ),
                      )
                    }
                    placeholder="e.g. 2 tbsp olive oil"
                    className="bg-transparent py-2 font-body text-lg text-ink placeholder:text-ink-muted/50 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setIngredients((prev) =>
                        prev.length > 1
                          ? prev.filter((p) => p.key !== ing.key)
                          : prev,
                      )
                    }
                    className="label text-ink-muted opacity-0 transition-all duration-200 hover:text-sienna group-hover:opacity-100"
                    aria-label="Remove ingredient"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() =>
                setIngredients((prev) => [
                  ...prev,
                  { key: uid(), raw_text: "" },
                ])
              }
              className="label ink-link mt-8 text-ink"
            >
              + Add ingredient
            </button>
          </section>

          <section
            className="col-span-12 md:col-span-8 animate-fade-up"
            style={{ animationDelay: "260ms" }}
          >
            <div className="mb-8 flex items-center gap-4">
              <div className="label text-sienna">N° 02</div>
              <div className="label text-ink-walnut">Method</div>
              <span className="hairline-ink flex-1" />
            </div>
            <ol className="space-y-10">
              {steps.map((st, idx) => (
                <li
                  key={st.key}
                  className="group grid grid-cols-[auto_1fr_auto] items-start gap-x-6"
                >
                  <div className="font-display text-5xl font-light leading-none text-sienna">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <textarea
                    value={st.text}
                    onChange={(e) =>
                      setSteps((prev) =>
                        prev.map((p) =>
                          p.key === st.key
                            ? { ...p, text: e.target.value }
                            : p,
                        ),
                      )
                    }
                    placeholder="Describe the step…"
                    rows={3}
                    className="resize-none border-0 border-b border-ink/10 bg-transparent py-2 font-body text-lg leading-relaxed text-ink placeholder:text-ink-muted/50 focus:border-ink focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setSteps((prev) =>
                        prev.length > 1
                          ? prev.filter((p) => p.key !== st.key)
                          : prev,
                      )
                    }
                    className="label mt-3 self-start text-ink-muted opacity-0 transition-all duration-200 hover:text-sienna group-hover:opacity-100"
                    aria-label="Remove step"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ol>
            <button
              type="button"
              onClick={() =>
                setSteps((prev) => [...prev, { key: uid(), text: "" }])
              }
              className="label ink-link mt-8 text-ink"
            >
              + Add step
            </button>
          </section>
        </div>

        <section className="mt-24 grid grid-cols-12 gap-x-8 gap-y-6 border-t border-ink/20 pt-12">
          <div className="col-span-12 md:col-span-3">
            <div className="label text-sienna">N° 03</div>
            <div className="label mt-1 text-ink-walnut">Marginalia</div>
          </div>
          <div className="col-span-12 md:col-span-9">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="A note on who made it, when, what we changed, why it matters…"
              className="w-full resize-none border-l-2 border-ink/15 bg-parchment-deep/60 px-6 py-6 font-body text-lg italic leading-relaxed text-ink placeholder:not-italic placeholder:text-ink-muted/70 focus:border-sienna focus:outline-none"
            />
          </div>
        </section>

        {error && (
          <div className="mt-8 border-l-2 border-sienna pl-4 font-body italic text-sienna animate-fade-in">
            {error}
          </div>
        )}

        <div className="mt-12 flex flex-wrap items-center gap-8 border-t border-ink/20 pt-10">
          <button type="submit" className="btn-ink" disabled={save.isPending}>
            {save.isPending
              ? "Pressing…"
              : isNew
                ? "Press to page"
                : "Save revisions"}
          </button>
          <button
            type="button"
            onClick={() => nav(-1)}
            className="label ink-link text-ink-walnut"
          >
            Cancel
          </button>
        </div>
      </form>
    </Shell>
  );
}
